import type {
  ChangeEvent,
  ClipboardEvent,
  CompositionEvent,
  CSSProperties,
  FocusEvent,
  FormEvent,
  KeyboardEvent,
  MouseEvent,
  MutableRefObject,
  PointerEventHandler,
  ReactNode
} from 'react';
import {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from 'react';
import styled, { css } from 'styled-components';
import type { FourValueTupleConvertable } from '@elfsight/widget-utils';
import {
  getInvertedColor,
  mergeMotionVariants,
  to4VTuple,
  transition
} from '@elfsight/widget-utils';
import equal from 'fast-deep-equal';
import type { TargetAndTransition } from 'framer-motion';
import { AnimatePresence, motion } from 'framer-motion';
import TextareaAutosize from 'react-textarea-autosize';
import { TextMeasure } from '../../text-measure';
import { ProgressiveMask } from '../ProgressiveMask';

const LABEL_SCALE_FACTOR = 0.75;
const COLOR_TRANSPARENT = 'rgba(0, 0, 0, 0)';
const LIMIT_MESSAGE_SHOW_THRESHOLD = 0.8;

export type TextControlBorderTypes = 'outline' | 'underline';

export type TextControlTypes =
  | 'text'
  | 'email'
  | 'number'
  | 'password'
  | 'tel'
  | 'url';

export interface TextControlBaseInstance {
  containerRef: MutableRefObject<HTMLDivElement | null>;
  inputRef: MutableRefObject<HTMLTextAreaElement | HTMLInputElement | null>;
  focusInput: (options?: FocusOptions) => void;
}

export type TextControlBaseVariants = Record<string, TargetAndTransition> & {
  initial?: TargetAndTransition;
  touched?: TargetAndTransition;
  filled?: TargetAndTransition;
  focused?: TargetAndTransition;
  disabled?: TargetAndTransition;
  limitAlmostReached?: TargetAndTransition;
  limitReached?: TargetAndTransition;
};

export type TextControlBaseSlotInner =
  | ReactNode
  | ((appliedVariants: string[]) => ReactNode);

export interface TextControlBaseProps {
  value?: string;
  initialValue?: string;
  label?: ReactNode;
  ariaLabel?: string;
  placeholder?: string;
  forceFocusAnimation?: boolean;
  leftSlot?: TextControlBaseSlotInner;
  rightSlot?: TextControlBaseSlotInner;
  limit?: number;
  multiline?: boolean;
  mask?: string;
  minVisibleRows?: number;
  maxVisibleRows?: number;
  autoFocus?: boolean;
  padding?: FourValueTupleConvertable<number>;
  variants?: TextControlBaseVariants;
  background?: string;
  fontStyle?: 'normal' | 'italic';
  fontWeight?: 'normal' | 'bold' | number;
  fontSize?: number;
  lineHeight?: number;
  shouldFocusSlotOnClick?: boolean;
  allowSlotNativeEvents?: boolean;
  specialEmptyCase?: string;
  fitContentWidth?: boolean;
  color?: string;
  borderType?: TextControlBorderTypes;
  borderColor?: string;
  borderWidth?: number;
  borderRadius?: number;
  underlineVariants?: TextControlBaseVariants;
  labelVariants?: TextControlBaseVariants;
  labelFontStyle?: 'normal' | 'italic';
  labelFontWeight?: 'normal' | 'bold' | number;
  labelFontSize?: number;
  labelTextTransform?: 'capitalize' | 'lowercase' | 'uppercase';
  labelColor?: string;
  placeholderVariants?: TextControlBaseVariants;
  placeholderFontStyle?: 'normal' | 'italic';
  placeholderFontWeight?: number;
  placeholderColor?: string;
  limitMessageVariants?: TextControlBaseVariants;
  limitMessageBackground?: string;
  customVariant?: string | string[];
  id?: string;
  name?: string;
  type?: TextControlTypes;
  disabled?: boolean;
  readOnly?: boolean;
  required?: boolean;
  className?: string;
  withoutTransition?: boolean;
  maxLength?: number;
  minLength?: number;
  inputMode?:
    | 'text'
    | 'email'
    | 'tel'
    | 'url'
    | 'none'
    | 'search'
    | 'numeric'
    | 'decimal'
    | undefined;
  style?: CSSProperties;
  onValueChange?: (
    value: string,
    event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => void;
  onFocus?: (event: FocusEvent<HTMLTextAreaElement | HTMLInputElement>) => void;
  onClick?: (event: MouseEvent<HTMLTextAreaElement | HTMLInputElement>) => void;
  onDoubleClick?: (
    event: MouseEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => void;
  onKeyDown?: (
    event: KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => void;
  onBlur?: (event: FocusEvent<HTMLTextAreaElement | HTMLInputElement>) => void;
  onPaste?: (
    event: ClipboardEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => void;
  onKeyUp?: (
    event: KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => void;
  onCompositionStart?: (
    event: CompositionEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => void;
  onCompositionEnd?: (
    event: CompositionEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => void;
}

export const TextControlBase = memo(
  forwardRef<TextControlBaseInstance, TextControlBaseProps>(
    function TextControlBase(
      {
        value,
        mask,
        initialValue = '',
        label,
        ariaLabel,
        placeholder,
        forceFocusAnimation,
        leftSlot,
        rightSlot,
        limit,
        multiline = false,
        minVisibleRows = 3,
        maxVisibleRows = Infinity,
        autoFocus = false,
        shouldFocusSlotOnClick = true,
        allowSlotNativeEvents = false,
        specialEmptyCase,
        padding = [10, 0],
        variants = {},
        background = 'rgba(255, 255, 255, 0)',
        fontStyle = 'normal',
        fontWeight = 400,
        fontSize = 16,
        lineHeight = 1.32,
        fitContentWidth,
        color = 'rgb(17, 17, 17)',
        borderType,
        borderColor = 'rgba(17, 17, 17, 0.3)',
        borderWidth = 1,
        borderRadius,
        underlineVariants = {},
        labelVariants = {},
        labelFontStyle = 'normal',
        labelFontWeight = 400,
        labelFontSize,
        labelTextTransform,
        labelColor = 'rgba(17, 17, 17, 0.5)',
        placeholderVariants = {},
        placeholderFontStyle = 'normal',
        placeholderFontWeight = 400,
        placeholderColor = 'rgba(17, 17, 17, 0.3)',
        limitMessageVariants = {},
        limitMessageBackground = 'rgb(255, 255, 255)',
        customVariant,
        id,
        name,
        type = 'text',
        disabled = false,
        required = false,
        readOnly = false,
        className,
        withoutTransition,
        minLength,
        maxLength,
        inputMode = 'text',
        style,
        onPaste,
        onValueChange,
        onFocus,
        onClick,
        onKeyDown,
        onBlur,
        onKeyUp,
        onDoubleClick,
        onCompositionStart,
        onCompositionEnd
      },
      forwardingRef
    ) {
      const controlled = value !== undefined;
      const [localValue, setLocalValue] = useState(initialValue);
      const effectiveValue = controlled ? (value as string) : localValue;

      useEffect(() => {
        setLocalValue(initialValue);
      }, [initialValue]);

      const [nativeValid, setNativeValid] = useState(true);
      const emptyInvalid = !effectiveValue && !nativeValid; // native 'number' input reset value while invalid
      const specialEmpty =
        !!specialEmptyCase && effectiveValue === specialEmptyCase;
      const filled = !specialEmpty && (!!effectiveValue || emptyInvalid);
      const withLabel = !!label;
      const withPlaceholder = !!placeholder;
      const withOutlineBorders = borderType === 'outline';
      const withUnderline = borderType === 'underline';
      const withLimit = limit !== undefined;
      const withMask = !!mask;

      const containerRef = useRef<HTMLDivElement | null>(null);
      const inputRef = useRef<HTMLTextAreaElement | HTMLInputElement | null>(
        null
      );

      const focusInput = useCallback((options?: FocusOptions) => {
        if (inputRef.current) {
          inputRef.current.focus(options);
        }
      }, []);

      useImperativeHandle(
        forwardingRef,
        () => ({
          containerRef,
          inputRef,
          focusInput
        }),
        [focusInput]
      );

      const format = (input: string) =>
        (multiline ? input : input.replace(/\r?\n|\r/g, '')).slice(0, limit);

      const handleChange = (
        event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
      ) => {
        const { value: rawValue } = event.target;
        const nextValue = format(rawValue);

        if (value === rawValue) {
          return;
        }

        if (!controlled) {
          setLocalValue(nextValue);
        }

        if (onValueChange) {
          onValueChange(nextValue, event);
        }
      };

      const handleInput = ({
        target
      }: FormEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        const valid = (target as HTMLTextAreaElement | HTMLInputElement)
          .validity.valid;

        setNativeValid(valid);
      };

      const [focused, setFocused] = useState(false);

      const handleInputFocus = (
        event: FocusEvent<HTMLTextAreaElement | HTMLInputElement>
      ) => {
        setFocused(true);

        if (onFocus) {
          onFocus(event);
        }
      };

      const handleInputBlur = (
        event: FocusEvent<HTMLTextAreaElement | HTMLInputElement>
      ) => {
        setFocused(false);

        if (onBlur) {
          onBlur(event);
        }
      };

      const handleTextControlBaseContainerClick = () => {
        focusInput();
      };

      const handleSlotPointerDown: PointerEventHandler<HTMLDivElement> = (
        event
      ) => {
        if (!shouldFocusSlotOnClick) {
          if (!allowSlotNativeEvents) {
            event.preventDefault();

            return;
          }

          return;
        }
        focusInput();
      };

      const inputProps = {
        id,
        name,
        disabled,
        'aria-required': required,
        autoFocus,
        value: format(effectiveValue),
        placeholder,
        onChange: handleChange,
        onInput: handleInput,
        onFocus: handleInputFocus,
        onBlur: handleInputBlur,
        onCompositionStart,
        onCompositionEnd,
        onClick,
        onKeyDown,
        onKeyUp,
        onDoubleClick,
        onPaste,
        maxLength,
        minLength,
        inputMode,
        readOnly
      };

      const { length: valueLength } = effectiveValue;
      const limitAlmostReached =
        withLimit &&
        valueLength / (limit as number) >= LIMIT_MESSAGE_SHOW_THRESHOLD;

      const limitReached = withLimit && valueLength >= (limit as number);

      const appliedVariants = [
        'initial',
        (filled || focused || forceFocusAnimation) && 'touched',
        filled && 'filled',
        (focused || forceFocusAnimation) && 'focused',
        limitAlmostReached && 'limitAlmostReached',
        limitReached && 'limitReached',
        disabled && 'disabled',
        ...(Array.isArray(customVariant) ? customVariant : [customVariant])
      ].filter(Boolean) as string[];

      const effectiveTransition = withoutTransition
        ? transition.disabled
        : {
            type: 'spring',
            stiffness: 600,
            damping: 45,
            mass: 1
          };

      const [paddingTop, paddingRight, paddingBottom, paddingLeft] =
        to4VTuple(padding);

      const realLineHeight = Math.round(fontSize * lineHeight);
      const inputFilledYOffset = withLabel
        ? (realLineHeight * LABEL_SCALE_FACTOR) / 2
        : 0;

      const limitMessageColor = getInvertedColor(limitMessageBackground);

      const renderSlot = (inner: TextControlBaseSlotInner) => (
        <TextControlBaseSlot
          onPointerDown={handleSlotPointerDown}
          style={{ height: realLineHeight }}
        >
          <div>
            {typeof inner === 'function' ? inner(appliedVariants) : inner}
          </div>
        </TextControlBaseSlot>
      );

      return (
        <TextMeasure text={effectiveValue} textStyle={{ fontSize, fontWeight }}>
          {({ width }) => (
            <TextControlBaseContainer
              ref={containerRef}
              className={className}
              style={{
                borderWidth,
                borderRadius,
                fontStyle,
                fontWeight,
                fontSize,
                lineHeight: `${realLineHeight}px`,
                width: fitContentWidth
                  ? width + paddingLeft + paddingRight + 4
                  : undefined,
                ...style
              }}
              _paddingTop={paddingTop}
              _paddingLeft={paddingLeft}
              _paddingRight={paddingRight}
              _paddingBottom={paddingBottom}
              transition={effectiveTransition}
              initial={false}
              animate={mergeMotionVariants(
                {
                  ...variants,
                  initial: {
                    borderColor: withOutlineBorders
                      ? borderColor
                      : COLOR_TRANSPARENT,
                    background,
                    color,
                    opacity: 1,
                    ...variants?.initial
                  },
                  disabled: {
                    opacity: 0.3,
                    ...variants?.disabled
                  }
                },
                appliedVariants
              )}
              onClick={handleTextControlBaseContainerClick}
            >
              <AnimatePresence initial={false}>
                {limitAlmostReached && (
                  <TextControlBaseLimitMessageWrapper
                    style={{ right: paddingRight }}
                    transition={effectiveTransition}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <TextControlBaseLimitMessage
                      transition={effectiveTransition}
                      initial={false}
                      animate={mergeMotionVariants(
                        {
                          ...limitMessageVariants,
                          initial: {
                            background: limitMessageBackground,
                            color: limitMessageColor,
                            ...limitMessageVariants?.initial
                          }
                        },
                        appliedVariants
                      )}
                    >
                      {valueLength}/{limit}
                    </TextControlBaseLimitMessage>
                  </TextControlBaseLimitMessageWrapper>
                )}
              </AnimatePresence>

              {!!leftSlot && renderSlot(leftSlot)}

              <TextControlBaseInner>
                {withLabel && (
                  <TextControlBaseLabelWrapper
                    transition={effectiveTransition}
                    initial={false}
                    animate={mergeMotionVariants(
                      {
                        initial: {
                          y: 0,
                          scale: 1
                        },
                        touched: {
                          y: -inputFilledYOffset,
                          scale: LABEL_SCALE_FACTOR
                        }
                      },
                      appliedVariants
                    )}
                  >
                    <TextControlBaseLabel
                      htmlFor={id}
                      style={{
                        fontStyle: labelFontStyle,
                        fontWeight: labelFontWeight,
                        fontSize: labelFontSize,
                        textTransform: labelTextTransform
                      }}
                      transition={effectiveTransition}
                      initial={false}
                      animate={mergeMotionVariants(
                        {
                          ...labelVariants,
                          initial: {
                            color: labelColor,
                            ...labelVariants?.initial
                          }
                        },
                        appliedVariants
                      )}
                    >
                      {label}
                    </TextControlBaseLabel>
                  </TextControlBaseLabelWrapper>
                )}

                <TextControlBaseInputWrapper
                  transition={effectiveTransition}
                  initial={false}
                  animate={mergeMotionVariants(
                    withLabel
                      ? {
                          initial: {
                            y: inputFilledYOffset,
                            opacity: 0
                          },
                          touched: { opacity: 1 }
                        }
                      : {
                          initial: {
                            y: 0,
                            opacity: 1
                          },
                          touched: { opacity: 1 }
                        },
                    appliedVariants
                  )}
                >
                  {withPlaceholder && !filled && (
                    <TextControlBasePlaceholder
                      style={{
                        fontStyle: placeholderFontStyle,
                        fontWeight: placeholderFontWeight
                      }}
                      transition={effectiveTransition}
                      initial={false}
                      animate={mergeMotionVariants(
                        {
                          ...placeholderVariants,
                          initial: {
                            color: placeholderColor,
                            ...placeholderVariants?.initial
                          }
                        },
                        appliedVariants
                      )}
                    >
                      {placeholder}
                    </TextControlBasePlaceholder>
                  )}
                  {withMask && (
                    <TextControlBaseMask
                      transition={effectiveTransition}
                      initial={false}
                      animate={mergeMotionVariants(
                        {
                          ...placeholderVariants,
                          initial: {
                            color: placeholderColor,
                            ...placeholderVariants?.initial
                          }
                        },
                        appliedVariants
                      )}
                    >
                      <ProgressiveMask
                        mask={mask}
                        value={effectiveValue}
                        fieldColor={placeholderColor}
                        fontWeight={fontWeight}
                        fontStyle={fontStyle}
                      />
                    </TextControlBaseMask>
                  )}
                  {multiline && (
                    <TextControlBaseTextarea
                      {...inputProps}
                      ref={inputRef as MutableRefObject<HTMLTextAreaElement>}
                      minRows={minVisibleRows}
                      maxRows={maxVisibleRows}
                      aria-label={ariaLabel}
                    />
                  )}
                  {!multiline && (
                    <TextControlBaseInput
                      {...inputProps}
                      ref={inputRef as MutableRefObject<HTMLInputElement>}
                      type={type}
                      autoComplete="off"
                      aria-label={ariaLabel}
                    />
                  )}
                </TextControlBaseInputWrapper>
              </TextControlBaseInner>

              {!!rightSlot && renderSlot(rightSlot)}

              {withUnderline && (
                <TextControlBaseUnderline
                  style={{
                    top: -borderWidth,
                    right: -borderWidth,
                    bottom: -borderWidth,
                    left: -borderWidth,
                    borderWidth
                  }}
                  transition={effectiveTransition}
                  initial={false}
                  animate={mergeMotionVariants(
                    {
                      ...underlineVariants,
                      initial: {
                        borderColor,
                        ...underlineVariants?.initial
                      }
                    },
                    appliedVariants
                  )}
                />
              )}
            </TextControlBaseContainer>
          )}
        </TextMeasure>
      );
    }
  ),
  (prevProps, nextProps) => {
    const {
      leftSlot: prevLeftSlot,
      rightSlot: prevRightSlot,
      ...otherPrevProps
    } = prevProps;

    const {
      leftSlot: nextLeftSlot,
      rightSlot: nextRightSlot,
      ...otherNextProps
    } = nextProps;

    return (
      equal(otherPrevProps, otherNextProps) &&
      prevLeftSlot === nextLeftSlot &&
      prevRightSlot === nextRightSlot
    );
  }
);

const inheritFontStyles = css`
  font-style: inherit;
  font-variant: inherit;
  font-weight: inherit;
  font-size: inherit;
  line-height: inherit;
  font-family: inherit;
  color: inherit;
  letter-spacing: inherit;
  text-transform: inherit;
`;

const input = css`
  ${inheritFontStyles};

  display: block;
  box-sizing: border-box;
  width: 100%;
  margin: 0;
  padding: 0;
  border: none !important;
  outline: none !important;
  box-shadow: none;
  background: transparent !important;
  background-color: transparent !important;
  text-overflow: ellipsis;

  /* fixes transparent carriage issue in Safari  */
  transform: translate3d(0, 0, 0);

  &::placeholder {
    opacity: 0;
  }
`;

export const TextControlBaseContainer = styled(motion.div)<{
  _paddingLeft: number;
  _paddingRight: number;
  _paddingTop: number;
  _paddingBottom: number;
}>`
  position: relative;
  display: flex;
  border: 1px solid;
  cursor: text;
  padding: ${({ _paddingTop, _paddingRight, _paddingBottom, _paddingLeft }) =>
    `${_paddingTop}px ${_paddingRight}px ${_paddingBottom}px ${_paddingLeft}px`} !important;
`;

export const TextControlBaseInner = styled(motion.div)`
  position: relative;
  flex: 1;
`;

export const TextControlBaseSlot = styled.div`
  display: flex;
  align-items: center;
  flex: 0;

  &:first-child {
    margin-right: 8px;
  }

  &:last-child {
    margin-left: 8px;
  }
`;

export const TextControlBaseInputWrapper = styled(motion.div)`
  position: relative;
  z-index: 1;
`;

export const TextControlBaseInput = styled.input`
  input& {
    ${input}
  }
`;

export const TextControlBaseTextarea = styled(TextareaAutosize)`
  textarea& {
    ${input}
  }

  resize: none;
`;

export const TextControlBaseLabelWrapper = styled(motion.div)`
  position: absolute;
  top: auto;
  left: auto;
  max-width: 100%;
  transform-origin: 2px 25%;
  pointer-events: none;
`;

export const TextControlBaseLabel = styled(motion.label)`
  ${inheritFontStyles};

  display: block;
  overflow: hidden;
  max-width: 100%;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

export const TextControlBasePlaceholder = styled(motion.div)`
  ${inheritFontStyles};

  position: absolute;
  overflow: hidden;
  top: 0;
  right: 0;
  left: 0;
  white-space: nowrap;
  text-overflow: ellipsis;
  pointer-events: none;
`;

export const TextControlBaseMask = styled(motion.div)`
  ${inheritFontStyles};

  position: absolute;
  overflow: hidden;
  top: 0;
  right: 0;
  left: 0;
  white-space: nowrap;
  text-overflow: ellipsis;
  pointer-events: none;
`;

export const TextControlBaseLimitMessageWrapper = styled(motion.div)`
  position: absolute;
  z-index: 3;
  top: -14px;
  pointer-events: none;
`;

export const TextControlBaseLimitMessage = styled(motion.div)`
  padding: 5px 8px;
  font-size: 13px;
  line-height: 1.38;
  border-radius: 4px;
  box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.3);
`;

export const TextControlBaseUnderline = styled(motion.div)`
  position: absolute;
  z-index: 2;
  border-bottom: solid;
  border-radius: inherit;
  pointer-events: none;
`;
